import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { chainApi } from "@/api/chain";
import { receivablesApi, type ReceivableRow } from "@/api/receivables";

const CENTRAL_HOSPITAL_SELECT_CENTRAL = "CENTRAL";
const ADD_NEW = "__add_new__";

function todayLocalISODate(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDateIst(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "numeric",
    month: "numeric",
    year: "numeric",
  });
}

function formatMoney(n: number): string {
  return `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function inferUnitFromHospitalName(name: string | null | undefined): string {
  if (name && /true\s*artis|artis\s*south\s*delhi/i.test(name)) return "LAJPAT_NAGAR";
  return "SEC_52";
}

function formatUnitLabel(unit: string | null | undefined): string {
  if (unit === "CENTRAL") return "Central";
  if (unit === "LAJPAT_NAGAR") return "TRUE ARTIS South Delhi";
  if (unit === "SEC_52") return "Rudram (Sec 52)";
  return unit || "—";
}

function paymentModeLabel(mode: string | null | undefined): string {
  if (!mode) return "—";
  return mode.replace(/_/g, " ");
}

export default function ReceivablesPage() {
  const { user, isChainAdmin, activeHospitalId } = useAuth();
  const qc = useQueryClient();
  const hospitalId = activeHospitalId || user?.hospitalId || "";
  const role = user?.role;
  const canEdit = role === "HOSPITAL_ADMIN" || role === "CHARTERED_ACCOUNTANT";
  const canMark = role === "HOSPITAL_ADMIN" || role === "CHARTERED_ACCOUNTANT";
  const isSuperAdminView = role === "SUPER_ADMIN";
  const readOnly = isSuperAdminView;

  const lockedUnit = useMemo(() => {
    const name =
      user?.hospitals?.find((h) => h.id === hospitalId)?.name ||
      user?.hospitals?.[0]?.name ||
      "";
    return inferUnitFromHospitalName(name);
  }, [hospitalId, user?.hospitals]);

  const { data: chainHospitals = [] } = useQuery({
    queryKey: ["chain-group-hospitals-receivables"],
    queryFn: async () => {
      const r = await chainApi.getGroup();
      const group = r.data?.data as { hospitals?: Array<{ id: string; name: string }> } | undefined;
      return group?.hospitals ?? [];
    },
    enabled: !!isChainAdmin,
    staleTime: 60_000,
  });

  const chainHospitalOptions = useMemo(() => {
    const opts = (chainHospitals || [])
      .filter((h) => h?.id && h?.name)
      .map((h) => ({
        value: h.id,
        label: h.name,
      }));
    return [...opts, { value: CENTRAL_HOSPITAL_SELECT_CENTRAL, label: "Central" }];
  }, [chainHospitals]);

  const defaultHospitalSelect = useMemo(() => {
    if (!isChainAdmin) return lockedUnit;
    const rudram = chainHospitalOptions.find((o) => /rudram/i.test(o.label));
    return rudram?.value ?? chainHospitalOptions[0]?.value ?? CENTRAL_HOSPITAL_SELECT_CENTRAL;
  }, [isChainAdmin, lockedUnit, chainHospitalOptions]);

  const [hospitalSelect, setHospitalSelect] = useState(CENTRAL_HOSPITAL_SELECT_CENTRAL);
  useEffect(() => {
    setHospitalSelect(isChainAdmin ? defaultHospitalSelect : lockedUnit);
  }, [isChainAdmin, defaultHospitalSelect, lockedUnit]);

  const { data: meta } = useQuery({
    queryKey: ["receivables-meta"],
    queryFn: () => receivablesApi.meta(),
  });

  const { data: typeNames = [], isLoading: typesLoading } = useQuery({
    queryKey: ["receivables-types"],
    queryFn: async () => (await receivablesApi.listLookups("RECEIVABLE_TYPE")).names || [],
  });

  const { data: counterpartyNames = [], isLoading: counterpartiesLoading } = useQuery({
    queryKey: ["receivables-counterparties"],
    queryFn: async () => (await receivablesApi.listLookups("COUNTERPARTY")).names || [],
  });

  const { data: rows = [], isLoading: listLoading } = useQuery({
    queryKey: ["receivables-list", hospitalId],
    queryFn: () => receivablesApi.list(),
    enabled: !!hospitalId,
  });

  const [receivableType, setReceivableType] = useState("");
  const [counterpartyName, setCounterpartyName] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [receivableDate, setReceivableDate] = useState(todayLocalISODate());
  const [dueDate, setDueDate] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [description, setDescription] = useState("");

  const [addDialog, setAddDialog] = useState<{
    kind: "RECEIVABLE_TYPE" | "COUNTERPARTY";
    open: boolean;
  }>({ kind: "RECEIVABLE_TYPE", open: false });
  const [addName, setAddName] = useState("");

  const [editRow, setEditRow] = useState<ReceivableRow | null>(null);
  const [markCollectedId, setMarkCollectedId] = useState<string | null>(null);
  const [markCollectedMode, setMarkCollectedMode] = useState("");
  const [markCollectedDate, setMarkCollectedDate] = useState(todayLocalISODate());

  const paymentModes = meta?.paymentModes?.length
    ? meta.paymentModes
    : ["CASH", "CARD", "UPI", "BANK_TRANSFER", "CHEQUE", "INSURANCE"];

  const createLookupMut = useMutation({
    mutationFn: async () => {
      const name = addName.trim();
      if (!name) throw new Error("Name is required");
      const body: {
        kind: "RECEIVABLE_TYPE" | "COUNTERPARTY";
        name: string;
        hospitalId?: string;
        hospitalUnit?: string;
      } = { kind: addDialog.kind, name };
      if (isChainAdmin) {
        if (hospitalSelect === CENTRAL_HOSPITAL_SELECT_CENTRAL) {
          body.hospitalUnit = "CENTRAL";
        } else {
          body.hospitalId = hospitalSelect;
        }
      }
      await receivablesApi.createLookup(body);
      return { kind: addDialog.kind, name };
    },
    onSuccess: ({ kind, name }) => {
      toast.success("Added to dropdown");
      setAddDialog((d) => ({ ...d, open: false }));
      setAddName("");
      if (kind === "RECEIVABLE_TYPE") {
        setReceivableType(name);
        void qc.invalidateQueries({ queryKey: ["receivables-types"] });
      } else {
        setCounterpartyName(name);
        void qc.invalidateQueries({ queryKey: ["receivables-counterparties"] });
      }
    },
    onError: (e: unknown) => {
      toast.error(e instanceof Error ? e.message : "Failed to add");
    },
  });

  const createMut = useMutation({
    mutationFn: async () => {
      const amt = Number(amount);
      if (!receivableType.trim()) throw new Error("Select or add a receivable type");
      if (!counterpartyName.trim()) throw new Error("Select or add from whom");
      if (!Number.isFinite(amt) || amt <= 0) throw new Error("Enter a valid amount");
      const body: Parameters<typeof receivablesApi.create>[0] = {
        receivableType: receivableType.trim(),
        counterpartyName: counterpartyName.trim(),
        amount: amt,
        description: description.trim() || undefined,
        invoiceNo: invoiceNo.trim() || undefined,
        receivableDate: receivableDate || undefined,
        dueDate: dueDate || undefined,
        paymentMode: paymentMode || undefined,
      };
      if (isChainAdmin) {
        if (hospitalSelect === CENTRAL_HOSPITAL_SELECT_CENTRAL) {
          body.hospitalUnit = "CENTRAL";
        } else {
          body.targetHospitalId = hospitalSelect;
        }
      } else {
        body.hospitalUnit = lockedUnit;
      }
      return receivablesApi.create(body);
    },
    onSuccess: () => {
      toast.success("Receivable saved");
      setAmount("");
      setDescription("");
      setInvoiceNo("");
      setPaymentMode("");
      setDueDate("");
      void qc.invalidateQueries({ queryKey: ["receivables-list"] });
      void qc.invalidateQueries({ queryKey: ["receivables-types"] });
      void qc.invalidateQueries({ queryKey: ["receivables-counterparties"] });
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e instanceof Error ? e.message : "Failed to save");
      toast.error(msg);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => receivablesApi.remove(id),
    onSuccess: () => {
      toast.success("Deleted");
      void qc.invalidateQueries({ queryKey: ["receivables-list"] });
    },
    onError: (e: unknown) => {
      toast.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          "Delete failed"
      );
    },
  });

  const markMut = useMutation({
    mutationFn: ({
      id,
      mode,
      collectedAt,
    }: {
      id: string;
      mode: string;
      collectedAt?: string;
    }) => receivablesApi.markCollected(id, mode, collectedAt),
    onSuccess: () => {
      toast.success("Marked collected");
      setMarkCollectedId(null);
      setMarkCollectedMode("");
      setMarkCollectedDate(todayLocalISODate());
      void qc.invalidateQueries({ queryKey: ["receivables-list"] });
    },
    onError: (e: unknown) => {
      toast.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          "Failed"
      );
    },
  });

  const unmarkMut = useMutation({
    mutationFn: (id: string) => receivablesApi.unmarkCollected(id),
    onSuccess: () => {
      toast.success("Marked unpaid");
      void qc.invalidateQueries({ queryKey: ["receivables-list"] });
    },
    onError: (e: unknown) => {
      toast.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          "Failed"
      );
    },
  });

  const updateMut = useMutation({
    mutationFn: async () => {
      if (!editRow) throw new Error("Nothing to edit");
      const amt = Number(amount);
      if (!receivableType.trim()) throw new Error("Receivable type required");
      if (!counterpartyName.trim()) throw new Error("From whom is required");
      if (!Number.isFinite(amt) || amt <= 0) throw new Error("Enter a valid amount");
      return receivablesApi.update(editRow.id, {
        receivableType: receivableType.trim(),
        counterpartyName: counterpartyName.trim(),
        amount: amt,
        description: description.trim() || undefined,
        invoiceNo: invoiceNo.trim() || undefined,
        receivableDate: receivableDate || undefined,
        dueDate: dueDate || undefined,
      });
    },
    onSuccess: () => {
      toast.success("Updated");
      setEditRow(null);
      resetForm();
      void qc.invalidateQueries({ queryKey: ["receivables-list"] });
    },
    onError: (e: unknown) => {
      toast.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          "Update failed"
      );
    },
  });

  function resetForm() {
    setReceivableType("");
    setCounterpartyName("");
    setAmount("");
    setPaymentMode("");
    setReceivableDate(todayLocalISODate());
    setDueDate("");
    setInvoiceNo("");
    setDescription("");
  }

  function startEdit(row: ReceivableRow) {
    setEditRow(row);
    setReceivableType(row.receivableType);
    setCounterpartyName(row.counterpartyName || "");
    setAmount(String(row.amount));
    setReceivableDate(row.receivableDate?.slice(0, 10) || todayLocalISODate());
    setDueDate(row.dueDate?.slice(0, 10) || "");
    setInvoiceNo(row.invoiceNo || "");
    setDescription(row.description || "");
    setPaymentMode("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function onTypeSelect(v: string) {
    if (v === ADD_NEW) {
      setAddDialog({ kind: "RECEIVABLE_TYPE", open: true });
      setAddName("");
      return;
    }
    setReceivableType(v === "__none__" ? "" : v);
  }

  function onCounterpartySelect(v: string) {
    if (v === ADD_NEW) {
      setAddDialog({ kind: "COUNTERPARTY", open: true });
      setAddName("");
      return;
    }
    setCounterpartyName(v === "__none__" ? "" : v);
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Receivables</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Money owed to the hospital. Cash Flow CF11B when marked collected. Access for hospital
          admin and accountant.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            {editRow ? "Edit receivable" : "New receivable"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Type of receivable</Label>
              <Select value={receivableType || "__none__"} onValueChange={onTypeSelect}>
                <SelectTrigger>
                  <SelectValue
                    placeholder={typesLoading ? "Loading…" : "Select type (or add new)"}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" disabled>
                    Select type
                  </SelectItem>
                  {typeNames.map((n) => (
                    <SelectItem key={n} value={n}>
                      {n}
                    </SelectItem>
                  ))}
                  <SelectItem value={ADD_NEW}>Add receivable type…</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Starts empty. Use “Add receivable type” once — it appears next time.
              </p>
            </div>

            <div className="space-y-2">
              <Label>From whom</Label>
              <Select
                value={counterpartyName || "__none__"}
                onValueChange={onCounterpartySelect}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      counterpartiesLoading ? "Loading…" : "Select vendor (or add new)"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" disabled>
                    Select from whom
                  </SelectItem>
                  {counterpartyNames.map((n) => (
                    <SelectItem key={n} value={n}>
                      {n}
                    </SelectItem>
                  ))}
                  <SelectItem value={ADD_NEW}>Add vendor name…</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Hospital / unit</Label>
              {isChainAdmin ? (
                <Select value={hospitalSelect} onValueChange={setHospitalSelect}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {chainHospitalOptions.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={formatUnitLabel(lockedUnit)} disabled />
              )}
              <p className="text-xs text-muted-foreground">
                Chain admin: pick a hospital, or Central for chain-level receivables.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Amount (₹)</Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Mode of payment (if already collected)</Label>
              <Select
                value={paymentMode || "__none__"}
                onValueChange={(v) => setPaymentMode(v === "__none__" ? "" : v)}
                disabled={!!editRow}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Not collected yet" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Not collected yet</SelectItem>
                  {paymentModes.map((m) => (
                    <SelectItem key={m} value={m}>
                      {paymentModeLabel(m)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                If you select a mode, this receivable is saved as Collected immediately.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Receivable date</Label>
              <Input
                type="date"
                value={receivableDate}
                onChange={(e) => setReceivableDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Receive by date</Label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Invoice / ref no.</Label>
              <Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional notes"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {editRow ? (
              <>
                <Button
                  onClick={() => updateMut.mutate()}
                  disabled={updateMut.isPending || !canEdit || readOnly}
                >
                  {updateMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Update
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditRow(null);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                onClick={() => createMut.mutate()}
                disabled={createMut.isPending || !canMark || readOnly}
              >
                {createMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save
              </Button>
            )}
          </div>
          {readOnly && (
            <p className="text-sm text-muted-foreground">
              Super admin view only — use a hospital admin or accountant account to book or edit.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Recent receivables</CardTitle>
        </CardHeader>
        <CardContent>
          {listLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6">No receivables booked yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Due</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Hospital</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-mono text-xs">
                        {row.receivableNumber || "—"}
                      </TableCell>
                      <TableCell>{row.receivableType}</TableCell>
                      <TableCell>{row.counterpartyName || "—"}</TableCell>
                      <TableCell className="text-right">{formatMoney(row.amount)}</TableCell>
                      <TableCell>{formatDateIst(row.dueDate)}</TableCell>
                      <TableCell>
                        {row.paymentStatus === "PAID" ? (
                          <Badge variant="outline" className="text-emerald-800 border-emerald-300">
                            Collected ({paymentModeLabel(row.paymentMode)})
                          </Badge>
                        ) : row.paymentStatus === "PARTIALLY_PAID" ? (
                          <Badge variant="outline">Partially paid</Badge>
                        ) : (
                          <Badge variant="outline">Unpaid</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.hospitalUnit === "CENTRAL"
                          ? "Central"
                          : row.hospital?.name || formatUnitLabel(row.hospitalUnit)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {canEdit && row.paymentStatus !== "PAID" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => startEdit(row)}
                            >
                              <Pencil className="h-3.5 w-3.5 mr-1" />
                              Edit
                            </Button>
                          )}
                          {canMark && row.paymentStatus !== "PAID" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setMarkCollectedId(row.id);
                                setMarkCollectedMode("");
                                setMarkCollectedDate(todayLocalISODate());
                              }}
                            >
                              Mark collected
                            </Button>
                          )}
                          {canEdit && row.paymentStatus === "PAID" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => unmarkMut.mutate(row.id)}
                              disabled={unmarkMut.isPending}
                            >
                              Unmark
                            </Button>
                          )}
                          {canEdit && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-destructive"
                              onClick={() => {
                                if (confirm("Delete this receivable entry?")) {
                                  deleteMut.mutate(row.id);
                                }
                              }}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={addDialog.open}
        onOpenChange={(open) => setAddDialog((d) => ({ ...d, open }))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Add {addDialog.kind === "RECEIVABLE_TYPE" ? "receivable type" : "vendor name"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Name</Label>
            <Input
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              placeholder={
                addDialog.kind === "RECEIVABLE_TYPE"
                  ? "e.g. Insurance claim, Corporate dues"
                  : "e.g. Vendor / company name"
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") createLookupMut.mutate();
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialog((d) => ({ ...d, open: false }))}>
              Cancel
            </Button>
            <Button onClick={() => createLookupMut.mutate()} disabled={createLookupMut.isPending}>
              {createLookupMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!markCollectedId}
        onOpenChange={(o) => !o && setMarkCollectedId(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark collected</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Mode of payment</Label>
              <Select
                value={markCollectedMode || "__none__"}
                onValueChange={(v) => setMarkCollectedMode(v === "__none__" ? "" : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" disabled>
                    Select mode
                  </SelectItem>
                  {paymentModes.map((m) => (
                    <SelectItem key={m} value={m}>
                      {paymentModeLabel(m)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Collected date (optional)</Label>
              <Input
                type="date"
                value={markCollectedDate}
                onChange={(e) => setMarkCollectedDate(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkCollectedId(null)}>
              Cancel
            </Button>
            <Button
              disabled={!markCollectedMode || !markCollectedId || markMut.isPending}
              onClick={() => {
                if (markCollectedId && markCollectedMode) {
                  markMut.mutate({
                    id: markCollectedId,
                    mode: markCollectedMode,
                    collectedAt: markCollectedDate || undefined,
                  });
                }
              }}
            >
              {markMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
